import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import {
  ArrowLeft, BarChart3, TrendingUp, Clock, ShoppingBag,
  Loader2, ArrowUpRight, ArrowDownRight
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AnalyticsPage() {
  const { token } = useAuth();
  const [period, setPeriod] = useState(30);
  const [overview, setOverview] = useState(null);
  const [topItems, setTopItems] = useState([]);
  const [peakHours, setPeakHours] = useState([]);
  const [dailyTrend, setDailyTrend] = useState([]);
  const [sourceBreakdown, setSourceBreakdown] = useState([]);
  const [catPerf, setCatPerf] = useState([]);
  const [loading, setLoading] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    const fetchAll = async () => {
      setLoading(true);
      try {
        const [ovRes, topRes, peakRes, trendRes, srcRes, catRes] = await Promise.all([
          fetch(`${API}/analytics/overview?days=${period}`, { headers }),
          fetch(`${API}/analytics/top-items?days=${period}`, { headers }),
          fetch(`${API}/analytics/peak-hours?days=${period}`, { headers }),
          fetch(`${API}/analytics/daily-trend?days=${period}`, { headers }),
          fetch(`${API}/analytics/source-breakdown?days=${period}`, { headers }),
          fetch(`${API}/analytics/category-performance?days=${period}`, { headers }),
        ]);
        if (ovRes.ok) setOverview(await ovRes.json());
        if (topRes.ok) setTopItems(await topRes.json());
        if (peakRes.ok) setPeakHours(await peakRes.json());
        if (trendRes.ok) setDailyTrend(await trendRes.json());
        if (srcRes.ok) setSourceBreakdown(await srcRes.json());
        if (catRes.ok) setCatPerf(await catRes.json());
      } catch { /* ignore */ } finally { setLoading(false); }
    };
    fetchAll();
  }, [token, period]);

  const maxHourOrders = Math.max(...peakHours.map(h => h.orders), 1);
  const maxDailyRevenue = Math.max(...dailyTrend.map(d => d.revenue), 1);
  const totalSourceRevenue = sourceBreakdown.reduce((s, r) => s + r.revenue, 0) || 1;

  const sourceLabel = { dine_in: 'Sur place', grab: 'Grab', takeaway: 'Emporter' };
  const sourceColor = { dine_in: '#D4A853', grab: '#00B14F', takeaway: '#E16833' };

  if (loading) return (
    <div className="min-h-screen bg-[#0C0C0C] flex items-center justify-center">
      <Loader2 className="w-8 h-8 text-[#D4A853] animate-spin" />
    </div>
  );

  return (
    <div className="min-h-screen bg-[#0C0C0C] text-white" data-testid="analytics-page">
      <header className="border-b border-[#1E1E1E] sticky top-0 z-40 bg-[#0C0C0C]/95 backdrop-blur-xl">
        <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin" className="text-[#666] hover:text-white"><ArrowLeft className="w-5 h-5" /></Link>
            <BarChart3 className="w-5 h-5 text-[#D4A853]" />
            <span className="font-bold text-lg">Analytics</span>
          </div>
          <div className="flex gap-1 bg-[#111] rounded-xl p-1 border border-[#1E1E1E]">
            {[7, 30, 90].map(d => (
              <button key={d} onClick={() => setPeriod(d)}
                className={`px-3 py-1.5 rounded-lg text-xs font-medium ${
                  period === d ? 'bg-[#D4A853] text-black' : 'text-[#888] hover:text-white'
                }`} data-testid={`period-${d}`}>
                {d}j
              </button>
            ))}
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        {/* KPI Cards */}
        {overview && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="analytics-kpis">
            {[
              { label: 'Chiffre d\'affaires', value: `${overview.total_revenue.toLocaleString()} THB`, icon: TrendingUp, color: '#D4A853' },
              { label: 'Commandes', value: overview.total_orders, icon: ShoppingBag, color: '#2D6A4F' },
              { label: 'Panier moyen', value: `${overview.average_order_value.toLocaleString()} THB`, icon: BarChart3, color: '#E16833' },
              { label: 'Pourboires', value: `${overview.total_tips.toLocaleString()} THB`, icon: ArrowUpRight, color: '#7C3AED' },
            ].map((kpi, i) => (
              <div key={i} className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-5">
                <div className="flex items-center gap-2 mb-2">
                  <kpi.icon className="w-4 h-4" style={{ color: kpi.color }} />
                  <span className="text-xs text-[#888]">{kpi.label}</span>
                </div>
                <p className="text-xl font-black" style={{ color: kpi.color }}>{kpi.value}</p>
              </div>
            ))}
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Top Items */}
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-5" data-testid="top-items">
            <h3 className="font-bold text-sm mb-4">Top plats (par CA)</h3>
            <div className="space-y-2">
              {topItems.length === 0 ? (
                <p className="text-sm text-[#666]">Aucune donnee</p>
              ) : topItems.slice(0, 8).map((item, i) => (
                <div key={i} className="flex items-center justify-between py-1.5">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[#666] w-5">{i + 1}.</span>
                    <span className="text-sm text-white">{item.name}</span>
                    <span className="text-[10px] text-[#888]">x{item.quantity}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-sm font-bold text-[#D4A853]">{item.revenue.toLocaleString()} THB</span>
                    {item.margin_pct !== undefined && (
                      <span className={`text-[10px] font-bold px-1.5 py-0.5 rounded ${
                        item.margin_pct > 50 ? 'bg-[#2D6A4F]/15 text-[#2D6A4F]' : 'bg-[#E16833]/15 text-[#E16833]'
                      }`}>{item.margin_pct}%</span>
                    )}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Source Breakdown */}
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-5" data-testid="source-breakdown">
            <h3 className="font-bold text-sm mb-4">Repartition par source</h3>
            <div className="space-y-3">
              {sourceBreakdown.map((src, i) => {
                const pct = Math.round((src.revenue / totalSourceRevenue) * 100);
                const color = sourceColor[src.source] || '#888';
                return (
                  <div key={i}>
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm font-medium" style={{ color }}>{sourceLabel[src.source] || src.source}</span>
                      <span className="text-sm text-[#CCC]">{src.revenue.toLocaleString()} THB ({pct}%)</span>
                    </div>
                    <div className="h-2 bg-[#1E1E1E] rounded-full overflow-hidden">
                      <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, background: color }} />
                    </div>
                    <span className="text-[10px] text-[#888]">{src.orders} commandes</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          {/* Peak Hours */}
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-5" data-testid="peak-hours">
            <h3 className="font-bold text-sm mb-4 flex items-center gap-2"><Clock className="w-4 h-4 text-[#D4A853]" /> Heures de pointe</h3>
            <div className="flex items-end gap-1 h-32">
              {Array.from({ length: 24 }, (_, h) => {
                const data = peakHours.find(p => p.hour === h);
                const orders = data?.orders || 0;
                const pct = (orders / maxHourOrders) * 100;
                return (
                  <div key={h} className="flex-1 flex flex-col items-center gap-1" title={`${h}h: ${orders} commandes`}>
                    <div className="w-full rounded-t" style={{
                      height: `${Math.max(pct, 2)}%`,
                      background: orders > maxHourOrders * 0.7 ? '#D4A853' : orders > 0 ? '#333' : '#1E1E1E',
                    }} />
                    {h % 4 === 0 && <span className="text-[8px] text-[#666]">{h}h</span>}
                  </div>
                );
              })}
            </div>
          </div>

          {/* Category Performance */}
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-5" data-testid="category-perf">
            <h3 className="font-bold text-sm mb-4">Performance par categorie</h3>
            <div className="space-y-2">
              {catPerf.length === 0 ? (
                <p className="text-sm text-[#666]">Aucune donnee</p>
              ) : catPerf.slice(0, 6).map((cat, i) => (
                <div key={i} className="flex items-center justify-between py-1">
                  <span className="text-sm text-white">{cat.category}</span>
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-[#888]">x{cat.quantity}</span>
                    <span className="text-sm font-bold text-[#D4A853]">{cat.revenue.toLocaleString()} THB</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Daily Trend */}
        {dailyTrend.length > 0 && (
          <div className="bg-[#111] border border-[#1E1E1E] rounded-2xl p-5" data-testid="daily-trend">
            <h3 className="font-bold text-sm mb-4">Tendance quotidienne</h3>
            <div className="flex items-end gap-1 h-40 overflow-x-auto">
              {dailyTrend.map((d, i) => {
                const pct = (d.revenue / maxDailyRevenue) * 100;
                return (
                  <div key={i} className="flex flex-col items-center gap-1 min-w-[24px]" title={`${d.date}: ${d.revenue.toLocaleString()} THB`}>
                    <div className="w-5 rounded-t bg-[#D4A853]" style={{ height: `${Math.max(pct, 3)}%` }} />
                    <span className="text-[7px] text-[#666] -rotate-45">{d.date.slice(5)}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
